import React, { useEffect, useState } from 'react';

import type { UUID } from '@aline/common';
import {
  CANNED_FIELD_METADATA,
  getIsAutoOnlyCannedVariant,
  isValidEmail,
  Select,
} from '@aline/common';
import clsx from 'clsx';
import scrollIntoView from 'scroll-into-view-if-needed';
import { AlineInput } from 'src/AlineInput';
import { AlineTooltip, useSetVariablePanelState, useVariablePanelState } from 'src/components';
import { CANNED_ICON_BY_VARIANT } from 'src/components/icons/CannedFieldIcons';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useDocumentSignatureStatus, useScrollCannedFieldIntoView } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import {
  getCanEditCannedFieldRow,
  getCannedFieldRowDisabledReason,
  getCannedFieldRowDisplayValue,
  getIsCannedFieldValueLocked,
} from './canned-field-row-state';
import { CannedFieldRowHeader } from './CannedFieldRowHeader';
import { useVariableStyles } from './styles';
import { useCannedFieldValueWrite } from './use-canned-field-value-write';

const useCannedRowStyles = makeStyles()(theme => ({
  endAdornment: {
    height: 18,
    marginRight: theme.spacing(1),
    width: 18,
  },
  subtitle: {
    color: theme.appColors.secondaryType,
    lineHeight: 1.3,
    marginBottom: theme.spacing(1),
  },
}));

interface CannedFieldRowProps extends React.HTMLAttributes<HTMLElement> {
  cannedField: Apollo.CannedFieldFullFragment;
  documentId: UUID;
}

export function CannedFieldRow({
  cannedField,
  className,
  documentId,
}: CannedFieldRowProps): JSX.Element {
  const currentUserId = useCurrentUserId();
  const setVariablePanelState = useSetVariablePanelState();
  const { focusedField } = useVariablePanelState();
  const { wasSentForSignature } = useDocumentSignatureStatus(documentId);
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const { scrollCannedFieldIntoView } = useScrollCannedFieldIntoView(tabId);
  const scrolledForIdRef = React.useRef<string | null>(null);
  const [liEl, setLiEl] = useState<HTMLLIElement | null>(null);
  const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null);
  const [value, setValue] = useState<string>(cannedField.value ?? '');
  const [isFocused, setIsFocused] = useState(false);

  const Icon = CANNED_ICON_BY_VARIANT[cannedField.variant];
  const { classes: cannedClasses } = useCannedRowStyles();
  const isAutoOnly = getIsAutoOnlyCannedVariant(cannedField.variant);
  const isEmail = cannedField.variant === Apollo.CannedFieldVariant.UserEmail;
  const isAssignedToCurrentUser =
    cannedField.assignee?.assignee?.id != null &&
    cannedField.assignee.assignee.id === currentUserId;
  const effectiveValue = cannedField.value ?? cannedField.defaultValue;
  const isValueLocked = getIsCannedFieldValueLocked(effectiveValue, isFocused);
  const canEdit = getCanEditCannedFieldRow({
    assigneeStatus: cannedField.assignee?.status,
    isAssignedToCurrentUser,
    isAutoOnly,
    isValueLocked,
    wasSentForSignature,
  });
  const isSelected = focusedField?.nodeId === cannedField.nodeId;
  const { classes } = useVariableStyles({ isCompleted: Boolean(value), isSelected });
  const { flushWrite, writeValue } = useCannedFieldValueWrite({
    cannedFieldId: cannedField.id,
    isEmail,
    savedValue: cannedField.value,
    setValue,
    workflowId: cannedField.workflowId,
  });

  useEffect(() => {
    if (isFocused) return;
    setValue(cannedField.value ?? '');
  }, [cannedField.value, isFocused]);

  useEffect(() => {
    if (!isSelected) {
      scrolledForIdRef.current = null;
      inputEl?.blur();
      return;
    }
    inputEl?.focus({ preventScroll: true });
    if (liEl) scrollIntoView(liEl, { behavior: 'smooth', scrollMode: 'if-needed' });
    if (scrolledForIdRef.current !== cannedField.nodeId) {
      scrolledForIdRef.current = cannedField.nodeId;
      scrollCannedFieldIntoView(cannedField.nodeId);
    }
  }, [cannedField.nodeId, inputEl, isSelected, liEl, scrollCannedFieldIntoView]);

  const emailError = isEmail && canEdit && value !== '' && !isValidEmail(value);
  const disabledReason = getCannedFieldRowDisabledReason({
    assigneeStatus: cannedField.assignee?.status,
    canEdit,
    isAssignedToCurrentUser,
    isAutoOnly,
    isValueLocked,
    variant: cannedField.variant,
    wasSentForSignature,
  });

  const displayValue = getCannedFieldRowDisplayValue({
    canEdit,
    defaultValue: cannedField.defaultValue,
    isAutoOnly,
    savedValue: cannedField.value,
    typedValue: value,
  });

  return (
    <li
      ref={setLiEl}
      className={clsx(className, classes.variable)}
      data-cy={Select.document.editor.rightDrawer.variable.list.row.container}
      tabIndex={0}
      onClick={() => {
        setVariablePanelState({
          focusedField: {
            __typename: 'CannedField',
            id: cannedField.id,
            nodeId: cannedField.nodeId,
          },
        });
      }}
    >
      <CannedFieldRowHeader
        cannedField={cannedField}
        documentId={documentId}
        isCompleted={Boolean(cannedField.value ?? cannedField.defaultValue)}
      />
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div>
          <AlineInput
            hideHelperText
            aria-label={CANNED_FIELD_METADATA[cannedField.variant].label}
            dataCy={Select.document.editor.rightDrawer.outline.cannedFieldRowInput}
            disabled={Boolean(disabledReason)}
            endAdornment={<Icon className={cannedClasses.endAdornment} />}
            error={emailError}
            helperText={emailError ? 'Please enter a valid email address.' : undefined}
            inputClassName={classes.input}
            inputRef={setInputEl}
            placeholder={cannedField.defaultValue ?? ''}
            type={isEmail ? 'email' : 'text'}
            value={displayValue}
            onBlur={() => {
              flushWrite();
              setIsFocused(false);
            }}
            onChange={e => writeValue(e.target.value)}
            onFocus={() => setIsFocused(true)}
          />
        </div>
      </AlineTooltip>
    </li>
  );
}
